import { useCallback, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "hooks";
import Notice from "components/tw/notice";
import { btnPrimaryCls, btnSecondaryCls } from "components/tw/classes";
import { setModalxComponent, setModalxComponentProps } from "components/modalx/modalx.slice";
import { QueueSkeleton } from "features/cabinet-queues/queue-list-states";
import { useQueueData } from "features/cabinet-queues/use-queue-data";
import { Translate } from "features/cabinet-tasks/task-row";
import { fetchCabinetFuelMonths, FuelMonthRow } from "services/cabinet.service";
import FuelMonthTable from "./fuel-month-table";
import { fuelMonthName } from "./month-name";

/** What the line over the table says: nothing missing, one named month, or how many months. */
function statusLine(missing: FuelMonthRow[], t: Translate): string {
  if (missing.length === 0) return t("cabinet_fuel_all_filed");
  if (missing.length > 1) return t("cabinet_fuel_missing_count", { count: missing.length });
  const { period } = missing[0];
  return t("cabinet_fuel_missing_month", { year: period.slice(0, 4), month: fuelMonthName(period, t) });
}

// A month written as the form takes it: 2026-09.
const MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;

/** The month and the vehicle a new report is opened for; the names are the form's own props. */
interface FormTarget {
  period: string;
  assetId: string;
}

const formTargetOf = (row: FuelMonthRow): FormTarget => ({ period: row.period, assetId: row.asset.id });

/**
 * «Kuro ataskaitos» in the cabinet: a line saying what is missing, one main button and a
 * table with a row for every month of every vehicle the person holds. The main button
 * fills in the oldest missing month.
 */
export default function CabinetFuelMonths() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { companyId = "", userId = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  // The form saves a report and asks the list to load again by moving this number.
  const refreshNonce = useAppSelector((state) => state.userAssetsFuelReportReducer.refreshNonce);
  const months = useQueueData(() => fetchCabinetFuelMonths(companyId), {
    scopeKey: companyId,
    requestKey: String(refreshNonce),
  });

  // Without a target the form starts at the current month with no vehicle.
  const openForm = useCallback(
    (target?: FormTarget) => {
      dispatch(setModalxComponent("FuelReportForm"));
      dispatch(setModalxComponentProps({ companyId, source: "profile", ...target }));
    },
    [dispatch, companyId]
  );

  // The «Užduotys» task links here with ?period=YYYY-MM&asset=<id>: the form opens at once
  // for that month and vehicle, and the address is cleaned so that a reload does not open
  // it again. The address can be typed by hand; a month that is no month opens the plain form.
  useEffect(() => {
    const period = searchParams.get("period");
    const assetId = searchParams.get("asset");
    if (!period || !assetId) return;
    openForm(MONTH_PATTERN.test(period) ? { period, assetId } : undefined);
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, openForm]);

  const rows = months.data ?? [];
  const missing = rows.filter((row) => row.status === "missing");
  // The server lists the newest month first, so the oldest missing one is the last.
  const oldestMissing = missing[missing.length - 1];
  const fillLabel = oldestMissing
    ? t("cabinet_fuel_fill_month", { month: fuelMonthName(oldestMissing.period, t) })
    : t("cabinet_fuel_fill");

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <p className={`text-base font-semibold ${missing.length > 0 ? "text-amber-700" : "text-gray-900"}`}>
          {months.data ? statusLine(missing, t) : ""}
        </p>
        <button
          type="button"
          className={`${btnPrimaryCls} w-full md:w-auto`}
          onClick={() => openForm(oldestMissing && formTargetOf(oldestMissing))}
        >
          {fillLabel}
        </button>
      </div>

      {months.hasFailed && (
        <Notice tone="critical" title={t("cabinet_fuel_load_failed")}>
          <button type="button" className={btnSecondaryCls} onClick={months.reload}>
            {t("try_again")}
          </button>
        </Notice>
      )}
      {!months.data && months.isLoading && <QueueSkeleton />}

      {rows.length > 0 && (
        <div className={months.isLoading ? "opacity-60" : ""}>
          <FuelMonthTable
            rows={rows}
            reportPath={(row) => `/app/${companyId}/profile/${userId}/fuel-reports/${row.reportId}`}
            onFill={(row) => openForm(formTargetOf(row))}
          />
          <p className="mt-3 text-sm text-gray-600">{t("cabinet_fuel_note")}</p>
        </div>
      )}
    </div>
  );
}
